import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChartOverlayComponent } from './ChartOverlayComponent';
//import axios from 'axios';
import { io } from 'socket.io-client';

import './styles.css';
import * as Globals from './globals'; // Import everything from utils
import Popup from './Popup';
import SoundToggle from './SoundToggle'; // Adjust the path as necessary
import GraphToggle from './GraphToggle'; // Adjust the path as necessary
import InfoButton from './InfoButton'; // Adjust the path as necessary
import InfoModal from './InfoModal'; // Adjust the path as necessary
import './InfoModal.css';
import ZoomBar from './ZoomBar'; // Adjust the path as necessary


const socket = io(process.env.REACT_APP_SOCKET_URL || undefined);

const CLIENT_RETENTION_MS = Number(
  process.env.REACT_APP_CLIENT_RETENTION_MS || 600000
);


const defaultGameRound = {
  expirationTime: 0,
  expirationTimeRemaining: 0,
  purchaseExpirationTime: 0,
  purchaseExpirationTimeRemaining: 0,
  inPlay: false,
  gamePhase: 0,
  bets: [],
  totalBetsAmount: 0,
  finalDataPoint: null,
  startTime: null,
  totalProfit: 0,
  totalReturn: 0,
  expectedProfit: 0,
};


function App() {
  const [zoomValue, setZoomValue] = useState(Globals.DURATION_PRE); // Initial zoom value

  const [data, setData] = useState([]);
  const [barData, setBarData] = useState([]);
  const [barsMap, setBarsMap] = useState(new Map());
  const chartRef = useRef(null); // Ref to access the chart instance
  const [betAmount, setBetAmount] = useState(Globals.defaultBetAmount);
  const [balance, setBalance] = useState(1000);  
  const [gameRound, setGameRound] = useState(defaultGameRound);
  const [xAxisMin, setXAxisMin] = useState(Date.now() - Globals.DURATION_PRE);
  const [xAxisMax, setXAxisMax] = useState(Date.now() + Globals.DURATION_POST);

  const [inGraph, setInGraph] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });
  
  const chartWrapperRef = useRef(null);

  const gameRoundRef = useRef(gameRound);
  const incrementedRef = useRef(false); // Track if the game phase has already been incremented
  const payoutAppliedRef = useRef(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [soundOn, setSoundOn] = useState(true); // State to control sound on/off
  const [barsOn, setBarsOn] = useState(true); // State to control sound on/off
  const [showModal, setShowModal] = useState(false);

  const toggleModal = () => {
    console.log("showing");
    setShowModal(!showModal);
  };

  const toggleSound = () => {
    setSoundOn(prevSoundOn => !prevSoundOn); // Toggle sound state
  };

  const toggleBars = () => {
    setBarsOn(prevBarsOn => !prevBarsOn);
  };

  const showInfo = () => {
    console.log("show info");
    toggleModal();
  }
  const displayPopup = (message) => {
    
    setPopupMessage(message);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };


  const playClickSound = (sound, soundOn1) => {
    
    if (soundOn) { // Check if the sound is toggled on
      if (sound) {
        sound.currentTime = 0; // Rewind to the start
        sound.play().catch(error => {
          console.error("Failed to play sound: ", error);
        });
      } else {
        console.error("Sound not found.");
      }
    } else {
      console.log("Sound is toggled off.");
    }
  };

  const handleMouseEnter = () => {
    setInGraph(true);
  };

  const handleMouseLeave = () => {
    setInGraph(false);
  };

  const handleMouseMove = (event) => {
    const rect = chartWrapperRef.current.getBoundingClientRect();
    setMousePosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };
  const handleTouchStart = (event) => {
    event.preventDefault(); // Prevent default touch behaviors
    setInGraph(true);
    handleTouchMove(event);
  };
  
  const handleTouchEnd = (event) => {
    event.preventDefault(); // Prevent default touch behaviors
    setInGraph(false);
  };
  
  const handleTouchMove = (event) => {
    event.preventDefault(); // Prevent default touch behaviors
    const touch = event.touches[0];
    const rect = chartWrapperRef.current.getBoundingClientRect();
    setMousePosition({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
  };


useEffect(() => {
  // 1) App height for iPhone dynamic viewport
  const setAppHeight = () => {
    const h = window.visualViewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty("--app-height", `${h}px`);
  };

  setAppHeight();
  window.addEventListener("resize", setAppHeight);
  window.visualViewport?.addEventListener("resize", setAppHeight);

  // 2) Pointer/touch listeners for the chart wrapper (if present)
  const chartWrapper = chartWrapperRef.current;

  if (chartWrapper) {
    chartWrapper.addEventListener("mouseenter", handleMouseEnter);
    chartWrapper.addEventListener("mouseleave", handleMouseLeave);
    chartWrapper.addEventListener("mousemove", handleMouseMove);

    chartWrapper.addEventListener("touchstart", handleTouchStart, { passive: false });
    chartWrapper.addEventListener("touchend", handleTouchEnd, { passive: false });
    chartWrapper.addEventListener("touchmove", handleTouchMove, { passive: false });
  }

  // 3) Always return cleanup
  return () => {
    window.removeEventListener("resize", setAppHeight);
    window.visualViewport?.removeEventListener("resize", setAppHeight);

    if (chartWrapper) {
      chartWrapper.removeEventListener("mouseenter", handleMouseEnter);
      chartWrapper.removeEventListener("mouseleave", handleMouseLeave);
      chartWrapper.removeEventListener("mousemove", handleMouseMove);

      chartWrapper.removeEventListener("touchstart", handleTouchStart);
      chartWrapper.removeEventListener("touchend", handleTouchEnd);
      chartWrapper.removeEventListener("touchmove", handleTouchMove);
    }
  };
}, []);


  useEffect(() => {
    gameRoundRef.current = gameRound;
  }, [gameRound]);


  
  useEffect(() => {

    
    socket.on('connect', () => {
      //console.log('Connected to WebSocket');
    });

    const processData = (arr) => {
    return (arr || [])
      .map((item) => {
        const tRaw = item?.time ?? item?.timestamp ?? item?.t ?? Date.now();
        const t = Number(tRaw);

        const candidates = [
          item?.tickDataLevel,
          item?.value,
          item?.v,
        ].map((x) => Number(x));

        // fallback: mid from bid/ask if present
        const bid = Number(item?.bid);
        const ask = Number(item?.ask);
        const mid = Number.isFinite(bid) && Number.isFinite(ask) ? (bid + ask) / 2 : NaN;

        const v = candidates.find(Number.isFinite) ?? mid;

        return { t, v };
      })
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
  };




  socket.on('initialData', (initialData) => {
    const processedData = processData(initialData);

    // Keep only what's visible (xAxisMin) and avoid sorting if server ordering is enforced
    const filtered = processedData.filter(item => item.t >= xAxisMin);

    setData(() => {
      // calculate bars once on initial load
      calculateBarData(filtered);
      return filtered;
    });
  });

  socket.on('tick', (point) => {
    if (!point) return;

    const processed = processData([point]);
    if (!processed.length) return;

    const newPoint = processed[0];

    // ignore ticks older than the visible window
    if (newPoint.t < xAxisMin) return;

    setData((prev) => {
      const next = prev.concat(newPoint);

      // retain 10 minutes at 200ms (recommended for demo)
      const cutoff = Date.now() - CLIENT_RETENTION_MS;
      const pruned = next.filter(item => item.t >= cutoff);

      // no sort needed if server emits ordered ticks
      calculateBarData(pruned);

      return pruned;
    });
  });


    

    
    

    return () => {
      socket.off('connect');
      socket.off('initialData');
      socket.off('tick');
    };
  }, []);

 
  


  const calculateBarData = useCallback((data) => {
    const bars = [];
    const interval = Globals.DURATION_BUCKET; // 5 seconds in milliseconds
  
    // Group data into 5-second buckets
    const buckets = data.reduce((acc, item) => {
      const bucketIndex = Math.floor(item.t / interval) * interval;
      if (!acc[bucketIndex]) {
        acc[bucketIndex] = [];
      }
      acc[bucketIndex].push(item);
      return acc;
    }, {});
  
    // Calculate bar data for each bucket and maintain existing min/max for old buckets
    for (const [bucketStart, bucket] of Object.entries(buckets)) {
      let bar;
      if (barsMap.has(bucketStart)) {
        // Use existing bar data for old buckets
        bar = barsMap.get(bucketStart);
      } else {
        const yStart = bucket[0].v;
        const yEnd = bucket[bucket.length - 1].v;
        const color = yEnd > yStart ? Globals.overColor : Globals.underColor; // Use CSS variables for colors
  
        const xStart = Number(bucketStart); // Start of the bucket
        const xEnd = xStart + interval; // End of the bucket (5 seconds later)
      
        //bucket width depends on data
        //const xStart = bucket[0].t;
        // Calculate the start of the current 5-second interval
        //const intervalStart = Math.floor(xStart / interval) * interval;
        // Calculate xEnd based on the interval start and duration
        //const xEnd = intervalStart + Globals.DURATION_BUCKET; // Use interval start + duration for xEnd
        
        
        const yMin = Math.min(...bucket.map(item => item.v));
        const yMax = Math.max(...bucket.map(item => item.v));
  
        bar = {
          x: (xStart + xEnd) / 2, // Center of the bucket
          xStart,
          xEnd,
          yStart,
          yEnd,
          yMin,
          yMax,
          backgroundColor: color,
        };
  
        // Store bar data in map
        barsMap.set(bucketStart, bar);
      }
  
      // If the current bucket is the latest one, update min/max values
      const latestTimestamp = data[data.length - 1].t;
      if (latestTimestamp >= bar.xStart && latestTimestamp <= bar.xEnd) {
        bar.yMin = Math.min(bar.yMin, ...bucket.map(item => item.v));
        bar.yMax = Math.max(bar.yMax, ...bucket.map(item => item.v));
        bar.yEnd = bucket[bucket.length - 1].v;
        // Update the background color based on yEnd and yStart
        bar.backgroundColor = bar.yEnd > bar.yStart ? Globals.overColor : Globals.underColor;
      }
  
      // Update bar in map
      barsMap.set(bucketStart, bar);
      bars.push(bar);
    }
    setBarsMap(new Map(barsMap)); // Update barsMap state
    setBarData(bars);
  }, [barsMap]);
  

  useEffect(() => {
    if (data.length > 0) {
      const currentTime = Date.now();
      setXAxisMin(currentTime - zoomValue);
      setXAxisMax(Date.now() + Globals.DURATION_POST);
     
    }
  }, [data, zoomValue]);  
  
  


  const calculateExpirationTime = () => {

    const now = new Date();
    let futureTime = now.getTime() + 30000; // At least 30 seconds in advance
    let futureDate = new Date(futureTime);

    // Round up to the nearest minute
    futureDate.setSeconds(0);
    futureDate.setMilliseconds(0);
    if (futureTime % 60000 > 0) {
      futureDate.setMinutes(futureDate.getMinutes() + 1);
    }

    return futureDate;

  };


const incrementGamePhase = () => {
    setGameRound((prevRound) => ({
      ...prevRound,
      gamePhase: (prevRound.gamePhase + 1) % 4, // Assuming gamePhase ranges from 0 to 3
    }));
  };

  

const handleBet = (betType) => {
    // Access the current balance using the balance state variable
    const currentBalance = balance; 
    const amount = betAmount; // Use betAmount as the bet amount

    if (!checkBet(balance, amount) || gameRound.gamePhase >= 2) return;

    const timePlaced = Date.now(); // Record the time the bet is placed
    const result = 'pending'; 
    const current = data[data.length - 1];
    if (!current || !Number.isFinite(current.v)) return;

    setBalance(prevBalance => prevBalance - amount);

    //console.log("BET placed at", new Date(timePlaced).toLocaleTimeString());
    //console.log("currentValue:", current.v);
    setGameRound((prevRound) => {
      const newBets = [...prevRound.bets, {
        timePlaced: timePlaced,
        betType: betType,
        amount: betAmount,
        entry: {
          t: current.t,
          v: current.v
        },
        result: result,
        return: 0,
        profit: 0,
      }];
      return {
        ...prevRound,
        gamePhase: 1,
        bets: newBets,
        totalBetsAmount: prevRound.totalBetsAmount + betAmount,
        startTime: prevRound.startTime || timePlaced, // Set startTime if it's not already set
      };
    });
    playClickSound(Globals.sounds.clickSound, soundOn);
  
  }

  const checkBet = (prevBalance,amount)  =>{
    if (prevBalance - amount >= 0)
    {
      return(true);
    }
    else{
      console.log("no funds");
      displayPopup("INSUFFIECIENT FUNDS!")
      return(false);
    }
    
  }

  const handleNewTrade = () => {
    payoutAppliedRef.current = false;   // reset payout guard
    setGameRound(defaultGameRound);
    playClickSound(Globals.sounds.alertSound, soundOn);
  };

  const handleZoomChange = useCallback((newZoomValue) => {
    setZoomValue(newZoomValue); // Update zoomValue state
  }, []);


const calculateGameOutcome = (isFinal) => {
  let totalProfit = 0;
  let totalReturn = 0;

  const ref = isFinal
    ? gameRound.finalDataPoint
    : data[data.length - 1];

  if (!ref || !Number.isFinite(ref.v)) {
    return {
      totalProfit: 0,
      totalReturn: 0,
      updatedBets: gameRound.bets,
    };
  }

  const updatedBets = gameRound.bets.map((bet) => {
    const entryV = Number(bet?.entry?.v);
    const refV = Number(ref.v);

    if (!Number.isFinite(entryV) || !Number.isFinite(refV)) {
      return bet;
    }

    const win =
      (bet.betType === "higher" && refV > entryV) ||
      (bet.betType === "lower" && refV < entryV);

    let result = bet.result;
    let profit = bet.profit ?? 0;
    let ret = bet.return ?? 0;

    if (isFinal) {
      if (refV === entryV) {
        result = "even";
        ret = bet.amount;
        totalReturn += ret;
      } else if (win) {
        profit = bet.amount * Globals.RTP;
        ret = bet.amount + profit;
        result = "win";
        totalReturn += ret;
        totalProfit += profit;
      } else {
        result = "loss";
      }
    } else {
      // expected profit preview
      if (win) {
        totalProfit += bet.amount * Globals.RTP;
      } else {
        totalProfit -= bet.amount;
      }
    }

    return {
      ...bet,
      result,
      profit,
      return: ret,
    };
  });

  return { totalProfit, totalReturn, updatedBets };
};




  useEffect(() => {
    gameRoundRef.current.bets = gameRound.bets;
  }, [gameRound.bets]);

  const handleExpiration = () => {
    const expiration = calculateExpirationTime();
    setGameRound((prevRound) => ({
      ...prevRound,
      expirationTime: expiration,
      inPlay: !prevRound.inPlay,
    }));
  };
  
  useEffect(() => {

    let animationFrameId; // Declare this outside of the useEffect

    const updateExpirationTimes = () => {
        const currentNow = new Date().getTime();
        //console.log("gamePhaseRef:",gamePhaseRef.current);
        switch (gameRoundRef.current.gamePhase) {
          case 0:
            const expiration = calculateExpirationTime();
            const expTime = expiration.getTime();
            const newPurchaseExpirationTime = expTime - 30000;
            setGameRound((prevRound) => ({
              ...prevRound,
              expirationTime: expTime,
              purchaseExpirationTime: newPurchaseExpirationTime,
              expirationTimeRemaining: Math.max(0, Math.round((expTime - currentNow) / 1000)), // Difference in seconds
              purchaseExpirationTimeRemaining: Math.max(0, Math.round((newPurchaseExpirationTime - currentNow) / 1000)), // Difference in seconds
            }));
            break;
          case 1:
            const currentExpTime = gameRoundRef.current.expirationTime;
            const currentPurchaseExpTime = gameRoundRef.current.purchaseExpirationTime;
            setGameRound((prevRound) => ({
              ...prevRound,
              expirationTimeRemaining: Math.max(0, Math.round((currentExpTime - currentNow) / 1000)), // Difference in seconds
              purchaseExpirationTimeRemaining: Math.max(0, Math.round((currentPurchaseExpTime - currentNow) / 1000)), // Difference in seconds
            }));



            const { totalProfit: expectedProfit1 } = calculateGameOutcome(false);
            setGameRound((prevRound) => ({
                ...prevRound,
                expectedProfit: expectedProfit1,
            }));
            break;
          case 2:
            const finalExpTime = gameRoundRef.current.expirationTime;
            setGameRound((prevRound) => {
              const finalRemainingTime = Math.max(0, Math.round((finalExpTime - currentNow) / 1000)); // Difference in seconds
              if (finalRemainingTime === 0) {
                console.log("TRADE ENDED ON:", data[data.length - 1]);
                return {
                  ...prevRound,
                  expirationTimeRemaining: finalRemainingTime,
                  finalDataPoint: data[data.length - 1],
                };
              }
              return {
                ...prevRound,
                expirationTimeRemaining: finalRemainingTime,
              };});
              // Calculate expected return
              const { totalProfit: expectedProfit2 } = calculateGameOutcome(false);
              
              setGameRound((prevRound) => ({
                  ...prevRound,
                  expectedProfit: expectedProfit2,
              }));

            break;
          case 3:
            //console.log("gamePhase is 3");
            // Add specific code for case 3 if needed
            break;
          default:
            console.log("Unknown gamePhase");
        }
      
        if (gameRoundRef.current.inPlay) {
          console.log("inPlayTRUE");
        }
        // Request the next frame
        animationFrameId = requestAnimationFrame(updateExpirationTimes);      
    };
    // Start the first frame update
    animationFrameId = requestAnimationFrame(updateExpirationTimes);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
}, [data]); 
  

useEffect(() => {
  if (gameRound.purchaseExpirationTimeRemaining === 0 && gameRound.gamePhase === 1) {
    console.log("PURCHASE ENDED");
    incrementGamePhase();
  }
}, [gameRound.purchaseExpirationTimeRemaining, gameRound.gamePhase]);

useEffect(() => {
  if (gameRound.expirationTimeRemaining === 0 && gameRound.gamePhase === 2) {
    console.log("TRADE ENDED");
    playClickSound(Globals.sounds.finishedSound, soundOn);
    setGameRound((prevRound) => ({
      ...prevRound,
      finalDataPoint: data[data.length - 1],
    }));
  }
}, [gameRound.expirationTimeRemaining, gameRound.gamePhase, data]);





useEffect(() => {
  if (!gameRound.finalDataPoint) return;
  if (payoutAppliedRef.current) return;

  const { totalProfit, totalReturn, updatedBets } = calculateGameOutcome(true);

  // 1) Update the round state (for UI)
  setGameRound((prevRound) => ({
    ...prevRound,
    totalProfit,
    totalReturn,
    bets: updatedBets,
  }));

  // 2) Pay out once
  payoutAppliedRef.current = true;
  setBalance((prev) => prev + (Number(totalReturn) || 0));

  incrementGamePhase();
}, [gameRound.finalDataPoint]);


return (
  <div className="App">
   
   {showPopup && (
        <Popup message={popupMessage} onClose={closePopup} />
      )}

    <div className="top-container">
      <div className="left-content">
        <span className="logoText">TRADE MASTER</span>
      </div>
      <div className="balance-box">
        <span className="label">BALANCE:</span>
        <span className="amount">{Globals.currencySymbol}{balance.toFixed(2)}</span>
      </div>
    </div>
    
    <div className="chart-container">
      <div className="chart-wrapper" ref={chartWrapperRef}>    
        <ChartOverlayComponent
          data={data}
          barData={barData}
          xAxisMin={xAxisMin}
          xAxisMax={xAxisMax}
          gameRound={gameRound} // Pass the entire gameRound object
          inGraph={inGraph} // Pass the inGraph state as a prop
          mousePosition={mousePosition} // Pass the mousePosition state as a prop
          barsOn={barsOn}
        />
      </div>
    </div>

    <div className='settings-row'>
      <ZoomBar onZoomChange={handleZoomChange} />
    </div>

    <div className='settings-row'>
      <div className='all-in-one-info'>
        <div className='info'>
          <div className='info-labels'>
            <label htmlFor="infoFX" className='info-label'> FX</label>
            <label className="info-label"></label>
            <label className="info-label">OPTIONS</label>
          </div>
          <div className='info-controls'>
            <div className='stake-buttons'>
                <span>BTC/USD</span>
            </div>
            <div className='stake-buttons'>
                
            </div>
            <div className='stake-buttons'>
                <GraphToggle barsOn={barsOn} toggleBars={toggleBars} />
                <SoundToggle soundOn={soundOn} toggleSound={toggleSound} />
                <InfoButton showInfo={toggleModal}></InfoButton>
                 <InfoModal show={showModal} handleClose={toggleModal} />

            </div>

          </div>
          
        </div>

      </div>
    </div>


    <div className='settings-row'>
      <div className='all-in-one-stake-input'>
        <div className='stake'>
          <div className='stake-labels'>
            <label htmlFor="stake-input" className='stake-label'> Stake (USD)</label>
            <label className="quick-stake-label">Quick Stake</label>
          </div>
          <div className='stake-controls'>
            <div className ='stake-input'>
              <button className='stake-decrBtn'  onClick={() => setBetAmount(Math.max(1, betAmount - 10))}>
                <span className='mat-icon'>
                  <svg viewBox="0 0 24 24" fit="" height="100%" width="100%" preserveAspectRatio="xMidYMid meet" focusable="false">
                  <rect x="6" y="11" width="12" height="2" rx="1" transform="translate(0 0)" fillRule="evenodd"></rect>
                  </svg>
                </span>
              </button>
              <input
                type="number"
                name="arrow"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                min="1"
                className="no-arrows"
              />
              <button className='stake-IncrBtn'   onClick={() => setBetAmount(betAmount + 10)}>
                <span className='mat-icon'>
                  <svg viewBox="0 0 24 24" fit="" height="100%" width="100%" preserveAspectRatio="xMidYMid meet" focusable="false">
                    <g fill="none" >
                      <path d="M0 0h24v24H0z"></path>
                      <path fill="currentColor" d="M12 6c.5522847 0 1 .44771525 1 1v4h4c.5522847 0 1 .4477153 1 1s-.4477153 1-1 1h-4v4c0 .5522847-.4477153 1-1 1s-1-.4477153-1-1v-4H7c-.55228475 0-1-.4477153-1-1s.44771525-1 1-1h4V7c0-.55228475.4477153-1 1-1Z"></path>
                    </g>
                  </svg>

                </span>
              </button>
            </div>

            <div className='stake-buttons'>
              <button onClick={() => setBetAmount(20)}>20</button>
              <button onClick={() => setBetAmount(50)}>50</button>
              <button onClick={() => setBetAmount(200)}>200</button>
            </div>
          </div>
        </div>

        <div className='CTA'>
          <div className="buttons">
            {gameRound.gamePhase < 2 ? (
            <>
              <button
                className="higher"
                onClick={() => handleBet('higher')}
              >
                <svg className="svg-icon higher-icon" width="20" height="25" viewBox="0 -5 48 60" fill="fill={Globals.backColor}"><path d="M47.6,19.01l-2.04-8.86c-0.1-0.45-0.55-0.73-1-0.63l-8.86,2.03c-0.45,0.1-0.5,0.39-0.11,0.63l3.28,2.05  c0.39,0.24,0.51,0.76,0.26,1.15l-5.52,8.81c-0.24,0.39-0.59,0.36-0.76-0.06l-5.72-13.91c-0.33-0.81-1.12-1.33-2-1.29  c-0.87,0.03-1.63,0.6-1.91,1.42l-6,17.88c-0.15,0.44-0.48,0.48-0.74,0.11l-4.74-6.82c-0.42-0.6-1.12-0.94-1.84-0.89  c-0.73,0.04-1.38,0.46-1.72,1.1L0.62,36c-0.54,1.02-0.15,2.28,0.86,2.82c0.31,0.17,0.65,0.24,0.98,0.24c0.75,0,1.47-0.4,1.84-1.11  l5.59-10.51c0.22-0.41,0.61-0.43,0.87-0.05l5.16,7.43c0.45,0.65,1.23,0.99,2.01,0.88c0.78-0.11,1.43-0.65,1.68-1.4l5.54-16.51  c0.15-0.44,0.41-0.45,0.58-0.02l5.14,12.49c0.3,0.72,0.97,1.22,1.75,1.29c0.78,0.07,1.53-0.31,1.95-0.97l8.11-12.95  c0.24-0.39,0.76-0.51,1.15-0.26l3.25,2.03C47.47,19.63,47.7,19.46,47.6,19.01z"/></svg>
                HIGHER
              </button>
              <button
                className="lower"
                onClick={() => handleBet('lower')}
              >
                <svg className="svg-icon lower-icon" width="20" height="25" viewBox="0 -5 48 60" fill="fill={Globals.backColor}"><path d="M47.6,19.01l-2.04-8.86c-0.1-0.45-0.55-0.73-1-0.63l-8.86,2.03c-0.45,0.1-0.5,0.39-0.11,0.63l3.28,2.05  c0.39,0.24,0.51,0.76,0.26,1.15l-5.52,8.81c-0.24,0.39-0.59,0.36-0.76-0.06l-5.72-13.91c-0.33-0.81-1.12-1.33-2-1.29  c-0.87,0.03-1.63,0.6-1.91,1.42l-6,17.88c-0.15,0.44-0.48,0.48-0.74,0.11l-4.74-6.82c-0.42-0.6-1.12-0.94-1.84-0.89  c-0.73,0.04-1.38,0.46-1.72,1.1L0.62,36c-0.54,1.02-0.15,2.28,0.86,2.82c0.31,0.17,0.65,0.24,0.98,0.24c0.75,0,1.47-0.4,1.84-1.11  l5.59-10.51c0.22-0.41,0.61-0.43,0.87-0.05l5.16,7.43c0.45,0.65,1.23,0.99,2.01,0.88c0.78-0.11,1.43-0.65,1.68-1.4l5.54-16.51  c0.15-0.44,0.41-0.45,0.58-0.02l5.14,12.49c0.3,0.72,0.97,1.22,1.75,1.29c0.78,0.07,1.53-0.31,1.95-0.97l8.11-12.95  c0.24-0.39,0.76-0.51,1.15-0.26l3.25,2.03C47.47,19.63,47.7,19.46,47.6,19.01z"/></svg>
                LOWER
              </button>
            </>
          ) : gameRound.gamePhase === 2 ? (
            <button className="inactive" >
              <div style={{ fontSize: '30px', lineHeight: '30px' }}>...</div>
              <div></div>
            </button>
            
          ) : (
            // gamePhase > 2
            <button className="new-trade" onClick={handleNewTrade}>
              <div style={{ fontSize: '30px', lineHeight: '30px' }}>+</div>
              <div>New Bet</div>
            </button>
          )}
          
          </div>
        </div>
      </div>
    </div>

  </div>
 
 
  
  );
}

export default App;